import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  NavItem,
  TabPane,
  TabContent,
  PopoverBody,
  ModalBody,
  UncontrolledTooltip,
} from 'reactstrap';

import useApi from 'hooks/useApi';
import {
  saveAnswers,
  saveContactAnswers,
  setCurrentNotificationReviewId,
  setCurrentNotificationReview,
} from 'redux/actions/notificationReviewActions';
import { addBreadcrumb, removeBreadcrumb } from 'redux/actions/layoutActions';
import {
  setShowTypeNotifications,
  setCurrentNotification,
} from 'redux/actions/notificationActions';

import { closeAlert } from 'redux/actions/alertActions';

import { ReactComponent as ConversationIcon } from 'assets/icon-list-menu.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as ClientIcon } from 'assets/icon-consulting.svg';
import { ReactComponent as ArrowExchangeIcon } from 'assets/icon-arrow-exchange.svg';
import { ReactComponent as CommunicationIcon } from 'assets/icon-conversation.svg';

import { ButtonIcon } from 'shared/components/Buttons';
import { colors } from 'shared/defaultTheme';
import AlertModal from 'shared/components/AlertModal';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { emptyString } from 'shared/utils';
import {
  getDocumentDetails,
  getDocumentPath,
  getPathName,
  setClientDocument,
  getReadDocument,
  setNavigationPane,
  refreshPath,
} from 'redux/actions/documentActions';
import { DOCUMENTS_VIEW } from 'shared/constants/variants';
import PropTypes from 'prop-types';
import { loadDocument } from 'redux/actions/resultActions';
import ClientsComponent from './components/Clients';
import AdvisorForm from './components/AdvisorForm';
import Notifications from './components/Notifications';
import Chat from './components/Chat/Chat';
import Files from './components/Files/Files';
import {
  NotificationDetailWrapper,
  BackgroundBoxNotificationReview,
  NotifyPanelBackground,
  NavHome,
  NavLink,
  SearchComment,
  PopoverParticipants,
  SelectAdvisorModal,
  FullName,
  ParticipantCircle,
  CommunicationClientsModal,
} from './styles';
import ContactPanel from './components/ContactPanel';
import CommunicationClients from './components/CommunicationClients';

const NotificationReview = () => {
  const { customAreaApi } = useApi();
  const history = useHistory();
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const { cusNavId, isCustomer, mail } = useSelector((state) => state.auth);
  const { openAlert, alertType, alertMessage } = useSelector((state) => state.alertState);
  const theme = useSelector((state) => state.theme);
  const { mainColor } = theme;
  const { breadcrumbs } = useSelector((state) => state.layout);
  const { t } = useTranslation();
  const { currentNotification, showTypeNotifications } = useSelector((state) => state.notification);
  const {
    contactNotifyId,
    currentNotificationReview,
    currentNotificationReviewId,
    documents: notificationReviewDocs,
  } = useSelector((state) => state.notificationReview);

  const { currentClient } = useSelector((state) => state.clientState);
  const [showError, setShowError] = useState(false);
  const [showSuccessSaveDoc, setShowSuccessSaveDoc] = useState();

  const [activeTab, setActiveTab] = useState('1');
  const [searchValue, setSearchValue] = useState('');
  const [openAdvisorFormModal, setOpenAdvisorFormModal] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [isCommunicationClientModalOpen, setIsCommunicationClientModalOpen] = useState(false);

  const { documentsView } = useSelector((state) => state.document);
  const isClientView = documentsView === DOCUMENTS_VIEW.CLIENTS;

  useEffect(() => {
    return () => {
      dispatch(setCurrentNotificationReviewId(''));
      dispatch(setCurrentNotificationReview(null));
      dispatch(setCurrentNotification(null));
    };
  }, []);

  useEffect(() => {
    if (search && search.length) {
      const params = new URLSearchParams(search);
      const idNotifyParam = params.get('idNotify');
      // const clientIdParam = params.get('clientId');
      dispatch(setCurrentNotificationReviewId(idNotifyParam));
    }
  }, [search]);

  const [, getAnswersData] = customAreaApi(
    {
      url: `${window.API_V1}/cli/notifications/answers/${cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getAnswersMassiveData] = customAreaApi(
    {
      url: `${window.API_V1}/gen/not/answers/${cusNavId}/search`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getPathItem] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    dispatch(setCurrentNotification(null));
  };

  useEffect(() => {
    return () => {
      window.removeEventListener('popstate', onBackButtonEvent);
    };
  }, []);

  useEffect(() => {
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
    }
    dispatch(
      addBreadcrumb({
        active: false,
        label: 'general_home',
        link: '/home',
        isModify: false,
      })
    );
    dispatch(
      addBreadcrumb({
        label: 'notification_title',
        active: true,
        link: '/notifications',
        isModify: false,
      })
    );
    dispatch(
      addBreadcrumb({
        label: 'notification_detail',
        active: true,
        link: pathname,
        isModify: false,
      })
    );
  }, []);

  useEffect(() => {
    dispatch(setCurrentNotificationReviewId(currentNotification?.id || ''));
  }, [currentNotification]);

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  const onToggleSuccessSaveDoc = () => setShowSuccessSaveDoc((currentShow) => !currentShow);

  const onOkSuccessSaveDoc = () => {
    onToggleSuccessSaveDoc();
  };

  const getActiveTab = (numberTab) => {
    setActiveTab(numberTab);
  };

  const searchNotifications = async (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
      try {
        const {
          data: { data },
        } = !showTypeNotifications
          ? await getAnswersData({
              data: {
                filters: [
                  {
                    model: 'Notify',
                    field: 'Created',
                    asc: false,
                  },
                  {
                    model: 'Notify',
                    field: 'ClientId',
                    filter: [currentClient?.clientId || currentNotificationReview?.clientId],
                  },
                  {
                    model: 'Notify',
                    field: 'Id',
                    filter: [currentNotificationReviewId],
                  },
                  {
                    model: 'Notify',
                    field: 'Answers.Text',
                    filter: [value],
                    isRegex: true,
                  },
                ],
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            })
          : await getAnswersMassiveData({
              data: {
                filters: [
                  {
                    model: 'GeneralNotify',
                    field: 'Created',
                    asc: false,
                  },
                  {
                    model: 'GeneralNotify',
                    field: 'Id',
                    filter: [currentNotificationReviewId],
                  },
                  {
                    model: 'GeneralNotify',
                    field: 'Answers.Text',
                    filter: [value],
                    isRegex: true,
                  },
                ],
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            });
        dispatch(saveAnswers(data));
      } catch (error) {
        dispatch(saveAnswers([]));
      }
    } else {
      setSearchValue('');
      try {
        const {
          data: { data },
        } = !showTypeNotifications
          ? await getAnswersData({
              data: {
                filters: [
                  {
                    model: 'Notify',
                    field: 'Created',
                    asc: false,
                  },
                  {
                    model: 'Notify',
                    field: 'ClientId',
                    filter: [currentClient?.clientId || currentNotificationReview?.clientId],
                  },
                  {
                    model: 'Notify',
                    field: 'Id',
                    filter: [currentNotificationReviewId],
                  },
                  {
                    model: 'Notify',
                    field: 'Answers.Text',
                    filter: [''],
                    isRegex: true,
                  },
                ],
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            })
          : getAnswersMassiveData({
              data: {
                filters: [
                  {
                    model: 'Notify',
                    field: 'Created',
                    asc: false,
                  },
                  {
                    model: 'Notify',
                    field: 'Id',
                    filter: [currentNotificationReviewId],
                  },
                  {
                    model: 'Notify',
                    field: 'Answers.Text',
                    filter: [''],
                    isRegex: true,
                  },
                ],
                reduce: {
                  limit: 0,
                  skip: 0,
                },
              },
            });
        dispatch(saveAnswers(data));
      } catch (error) {
        dispatch(saveAnswers([]));
      }
    }
  };

  const onCloseAdvisorFormModal = () => {
    setOpenAdvisorFormModal(false);
  };

  const onCloseAlert = () => {
    if (!emptyString(contactNotifyId)) {
      dispatch(setShowTypeNotifications(false));
      dispatch(saveContactAnswers(''));
      dispatch(setCurrentNotificationReviewId(contactNotifyId));
    }
    dispatch(closeAlert());
  };

  const getDocumentById = async (id, currentClientId) => {
    try {
      const {
        data: { data },
      } = await getPathItem({
        url: `${window.API_V1}/cli/items/${cusNavId}/${currentClientId}/${encodeURIComponent(
          mail
        )}/${id}`,
      });
      const pathIds = data.pathId.split('/');
      const clearedPathIds = pathIds.filter((x) => x.length > 0);
      let currentPath = '';
      clearedPathIds.forEach((pathName) => {
        currentPath += `${pathName}/`;
      });
      dispatch(getDocumentPath(currentPath));
      dispatch(getPathName(data.pathName));
      if (isClientView) {
        dispatch(setClientDocument(true));
      }
      dispatch(loadDocument(true));
      dispatch(getDocumentDetails(null));
      dispatch(getReadDocument(null));
      dispatch(refreshPath(true));
      dispatch(setNavigationPane(true));
      history.push('/documentations');
    } catch (error) {
      console.error(`Error Documents: ${error}`);
    }
  };

  const goToFileManager = async () => {
    if (showTypeNotifications) {
      setIsCommunicationClientModalOpen(true);
    } else {
      const { idFolderFileManager, clientId } = currentNotificationReview;
      await getDocumentById(idFolderFileManager, clientId);
    }
  };

  const checkClientsAndContactsHasFolder = (clientsAndContacts) => {
    if (!clientsAndContacts) {
      return false;
    }
    const clientsAndContactsHasFolder = clientsAndContacts.filter(
      (clientAndContact) => clientAndContact.idFolderFileManager
    );
    return clientsAndContactsHasFolder.length > 0;
  };

  const hasFolderFileManager = () => {
    if (!showTypeNotifications && currentNotificationReview?.idFolderFileManager) {
      return true;
    }
    if (
      showTypeNotifications &&
      currentNotificationReview?.clientsAndContacts?.length > 0 &&
      checkClientsAndContactsHasFolder(currentNotificationReview?.clientsAndContacts)
    ) {
      return true;
    }
    return false;
  };

  const Description = ({ tabId }) => {
    return (
      <Row className="mb-3">
        <Col sm={12} className="description-wrapper">
          <span className="description-notify-title">
            <span className="description-notify-text">{currentNotificationReview?.name}</span>
          </span>
          {tabId === 2 && hasFolderFileManager() && isCustomer && (
            <div
              className="description-to-notify-link"
              onClick={goToFileManager}
              onKeyDown={goToFileManager}
              role="button"
              tabIndex="0"
            >
              <span>
                <CommunicationIcon fill={colors.zambezi} className="icon" />
              </span>
              <span>
                {t('notification_to_file_manager_label').replace(
                  '{folderName}',
                  `${currentNotificationReview?.name}`
                )}
              </span>
            </div>
          )}
        </Col>
      </Row>
    );
  };

  Description.propTypes = {
    tabId: PropTypes.number.isRequired,
  };

  const onCloseCommunicationClientsModal = () => {
    setIsCommunicationClientModalOpen(false);
  };

  return (
    <NotificationDetailWrapper
      className="d-flex pb-3"
      isCurrentNotification={currentNotificationReview}
    >
      {/* {errorNotify && <Redirect to="/home" />} */}
      {isCustomer ? <ClientsComponent /> : <ContactPanel />}
      <>
        <NotifyPanelBackground className="mr-4">
          <Notifications />
        </NotifyPanelBackground>
      </>
      <BackgroundBoxNotificationReview secondaryColor={theme.secondaryColor}>
        {currentNotificationReview && (
          <>
            <div>
              <div className="d-flex align-items-end justify-content-between mb-3 form w-100">
                <div className="search-comment-container">
                  <SearchComment
                    Icon={() => null}
                    borderRadius={2}
                    className="h-50"
                    inputClassName="w-100"
                    buttonClassName=""
                    withButtonClear
                    rightIcon={<SearchIcon fill={colors.black} />}
                    onChange={searchNotifications}
                    value={searchValue ?? ''}
                    placeholder={t('notification_search_comment')}
                  />
                </div>

                <div className="d-flex align-items-end w-nav-container justify-content-end">
                  <div className="container-participants" id="PopoverParticipants">
                    <ButtonIcon
                      title={t('general_participants')}
                      className="button-icon"
                      Icon={() => <ClientIcon fill={colors.zambezi} />}
                    />
                    <div className="number-participants-container">
                      <span>
                        {currentNotificationReview?.participants?.filter((x) => x.mail).length}
                      </span>
                    </div>
                  </div>
                  <PopoverParticipants
                    trigger="legacy"
                    placement="bottom"
                    target="PopoverParticipants"
                    mainColor={mainColor}
                    isOpen={showPopover}
                    toggle={() => {
                      setShowPopover(!showPopover);
                    }}
                  >
                    <PopoverBody className="w-100">
                      {currentNotificationReview?.participants
                        ?.filter((x) => x.mail)
                        .sort((a, b) => b.isInCharge - a.isInCharge)
                        .map((participant, index) => (
                          <div
                            key={participant.mail}
                            className="participant-list"
                            aria-hidden
                            onClick={() => {
                              if (participant.isInCharge && isCustomer) {
                                setOpenAdvisorFormModal(true);
                                setShowPopover(!showPopover);
                              }
                            }}
                          >
                            <ParticipantCircle
                              className="participant-circle"
                              active={participant.active}
                              mainColor={mainColor}
                            >
                              <span className="participant-circle-text text-uppercase">
                                {participant.fullName?.substring(0, 1)}
                              </span>
                            </ParticipantCircle>
                            <div className="d-flex w-100 justify-content-between">
                              <FullName
                                active={participant.active}
                                className="participant-full-text ml-1"
                                id={`notify-fullName-${index}`}
                              >
                                {participant.clientName && (
                                  <UncontrolledTooltip
                                    placement="top"
                                    target={`notify-fullName-${index}`}
                                    autohide={false}
                                  >
                                    {participant.clientName ?? ''}
                                  </UncontrolledTooltip>
                                )}

                                {participant.fullName}
                              </FullName>
                              {participant.isInCharge && isCustomer && (
                                <ArrowExchangeIcon fill={colors.black} width="18" height="18px" />
                              )}
                            </div>
                          </div>
                        ))}
                    </PopoverBody>
                  </PopoverParticipants>
                  <div className="w-50">
                    <NavHome tabs>
                      <NavItem>
                        <NavLink
                          color={activeTab === '1' ? mainColor : colors.pattens}
                          selected={activeTab === '1'}
                          onClick={() => getActiveTab('1')}
                        >
                          {t('general_chat')}
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          color={activeTab === '2' ? mainColor : colors.pattens}
                          selected={activeTab === '2'}
                          onClick={() => getActiveTab('2')}
                        >
                          {`${t('genera_files')} (${notificationReviewDocs.length})`}
                        </NavLink>
                      </NavItem>
                    </NavHome>
                  </div>
                </div>
              </div>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <Row>
                    <Col sm="12" className="">
                      <Description tabId={1} />
                      <Chat newName={currentNotificationReview?.name} />
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabId="2">
                  {activeTab === '2' && (
                    <Row>
                      <Col sm="12" className="">
                        <Description tabId={2} />
                        <Files />
                      </Col>
                    </Row>
                  )}
                </TabPane>
              </TabContent>
            </div>
          </>
        )}
        {!currentNotificationReview && (
          <div className="d-flex flex-column justify-content-center align-items-center communication-detail-not-found">
            <ConversationIcon fill={colors.suvaGrey} />
            <p>{t('communication_detail_not_found')}</p>
          </div>
        )}
        <SelectAdvisorModal
          isOpen={openAdvisorFormModal}
          toggle={onCloseAdvisorFormModal}
          size="md"
        >
          <ModalHeader
            className="modal-header"
            backgroundColor={colors.transparent}
            onClose={onCloseAdvisorFormModal}
          />
          <ModalBody>
            <AdvisorForm onCancel={onCloseAdvisorFormModal} />
          </ModalBody>
        </SelectAdvisorModal>
      </BackgroundBoxNotificationReview>
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={t('custom_field_operation_error_message')}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <AlertModal
        type="success"
        isOpen={showSuccessSaveDoc}
        toggle={onOkSuccessSaveDoc}
        message={t('custom_feild_send_document_success')}
        okText={t('general_button_ok')}
        onOk={onOkSuccessSaveDoc}
      />
      <AlertModal
        type={alertType}
        isOpen={openAlert}
        toggle={onCloseAlert}
        message={alertMessage}
        okText={t('general_button_ok')}
        onOk={onCloseAlert}
      />
      <CommunicationClientsModal
        isOpen={isCommunicationClientModalOpen}
        toggle={onCloseCommunicationClientsModal}
        size="md"
      >
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseCommunicationClientsModal}
        />
        <ModalBody>
          <CommunicationClients getDocumentById={getDocumentById} />
        </ModalBody>
      </CommunicationClientsModal>
    </NotificationDetailWrapper>
  );
};

export default NotificationReview;
